import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useScrollToTop } from '../hooks/useScrollToTop';

const TermsPage: React.FC = () => {
  // Scroll to top when page loads
  useScrollToTop();

  return (
    <>
      <Helmet>
        <title>Terms of Service - Destress Daily</title>
        <meta name="description" content="Terms of Service for Destress Daily - Learn about our terms, conditions, and policies for using our health and wellness platform." />
        <meta name="keywords" content="terms of service, destress daily, legal, privacy policy" />
        <meta property="og:title" content="Terms of Service - Destress Daily" />
        <meta property="og:description" content="Terms of Service for Destress Daily - Learn about our terms, conditions, and policies." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dailyhealthhacks.com/terms" />
        <link rel="canonical" href="https://dailyhealthhacks.com/terms" />
      </Helmet>

      <div className="pt-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary-50 via-white to-health-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-gradient rounded-2xl mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Terms of Service
              </h1>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Please read these terms carefully before using Destress Daily.
              </p>
            </div>
          </div>
        </div>

        {/* Terms Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="prose prose-lg max-w-none">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 md:p-12">
              
              <div className="mb-8">
                <p className="text-sm text-gray-500 mb-4">
                  <strong>Last updated:</strong> {new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>

              <div className="space-y-8">
                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    By accessing and using Destress Daily ("the Website"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    Destress Daily provides health and wellness information, tips, and educational content. Our content includes articles, tips, and advice related to fitness, nutrition, mental health, sleep, and general wellness.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Medical Disclaimer</h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    <strong>Important:</strong> The information provided on Destress Daily is for educational and informational purposes only. It is not intended as a substitute for professional medical advice, diagnosis, or treatment.
                  </p>
                  <p className="text-gray-600 mb-4">
                    Always seek the advice of your physician or other qualified health provider with any questions you may have regarding a medical condition. Never disregard professional medical advice or delay in seeking it because of something you have read on this website.
                  </p>
                  <p className="text-gray-600 mb-4">
                    The content on this website is not intended to diagnose, treat, cure, or prevent any disease.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">4. User Responsibilities</h2>
                  <p className="text-gray-600 mb-4">
                    By using this website, you agree to:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                    <li>Use the information provided responsibly and at your own risk</li>
                    <li>Consult with healthcare professionals before making significant changes to your health routine</li>
                    <li>Not rely solely on the information provided on this website for medical decisions</li>
                    <li>Use the website in compliance with all applicable laws and regulations</li>
                    <li>Not attempt to gain unauthorized access to any part of the website</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Content and Intellectual Property</h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    All content on Destress Daily, including but not limited to text, graphics, images, and information, is the property of Destress Daily and is protected by copyright laws.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    You may not reproduce, distribute, or create derivative works from this content without express written permission from Destress Daily.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Newsletter and Email Communications</h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    By subscribing to our newsletter, you agree to receive email communications from Destress Daily. You can unsubscribe at any time using the unsubscribe link provided in our emails.
                  </p>
                  <p className="text-gray-600 mb-4">
                    We respect your privacy and will not share your email address with third parties without your consent, except as required by law.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Limitation of Liability</h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    Destress Daily and its contributors shall not be liable for any direct, indirect, incidental, special, or consequential damages resulting from the use or inability to use the information provided on our website.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    This includes but is not limited to damages for loss of profits, data, or other intangible losses, even if Destress Daily has been advised of the possibility of such damages.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">8. External Links</h2>
                  <p className="text-gray-600 mb-4">
                    Our website may contain links to external websites. We are not responsible for the content, privacy policies, or practices of any third-party websites. We encourage you to review the terms and privacy policies of any external sites you visit.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Changes to Terms</h2>
                  <p className="text-gray-600 mb-4">
                    We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting on the website. Your continued use of the website after any changes constitutes acceptance of the new terms.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contact Information</h2>
                  <p className="text-gray-600 mb-4">
                    If you have any questions about these Terms of Service, please contact us at:
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-600">
                      <strong>Email:</strong> hello@destressdaily.com<br />
                      <strong>Website:</strong> https://destressdaily.com/contact
                    </p>
                  </div>
                </section>

                <div className="border-t border-gray-200 pt-8 mt-8">
                  <p className="text-sm text-gray-500">
                    By using Destress Daily, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsPage; 